import { useState, useRef, useEffect } from 'react'
import { ChevronDown, AlertCircle } from 'lucide-react'
import { cn } from '../../../lib/utils'

interface DropdownOption {
  value: string
  label: string
  description?: string
}

interface DropdownSelectProps {
  id?: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  options: DropdownOption[]
  error?: string
  ariaLabel?: string
  disabled?: boolean
}

export function DropdownSelect({
  id,
  value,
  onChange,
  placeholder = 'Select an option',
  options,
  error,
  ariaLabel,
  disabled = false,
}: DropdownSelectProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    if (open) document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const selected = options.find((o) => o.value === value)

  return (
    <div className="w-full relative" ref={ref} id={id}>
      <button
        type="button"
        aria-label={ariaLabel}
        onClick={() => !disabled && setOpen((s) => !s)}
        className={cn(
          'w-full min-h-10 px-3 py-2 bg-white border border-neutral-200 rounded-md text-sm text-left flex items-center justify-between',
          'focus-visible:outline-none focus-visible:border-neutral-800',
          open && 'border-neutral-800',
          error && 'border-status-error focus-visible:border-status-error',
          disabled && 'bg-neutral-50 cursor-not-allowed',
        )}
        aria-expanded={open}
        aria-haspopup="listbox"
        disabled={disabled}
      >
        <span
          className={cn(selected ? 'text-neutral-950' : 'text-neutral-400')}
        >
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown
          className={cn(
            'w-4 h-4 transition-transform text-neutral-600 ml-2 flex-shrink-0',
            open && 'rotate-180',
          )}
        />
      </button>
      {open && !disabled && (
        <div className="absolute z-10 w-full mt-1 bg-white text-neutral-950 border border-neutral-200 rounded-md shadow-lg max-h-60 overflow-auto">
          <div role="listbox">
            {options.map((opt) => (
              <button
                key={opt.value}
                type="button"
                role="option"
                aria-selected={value === opt.value}
                onClick={() => {
                  onChange(opt.value)
                  setOpen(false)
                }}
                className={cn(
                  'w-full px-3 py-2.5 text-left text-sm hover:bg-neutral-50 focus:bg-neutral-50 focus:outline-none',
                  value === opt.value && 'bg-neutral-100 font-medium',
                )}
              >
                <div className="flex flex-col">
                  <span>{opt.label}</span>
                  {opt.description && (
                    <span className="text-xs text-neutral-500 mt-0.5">
                      {opt.description}
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
      {error && (
        <span className="text-status-error text-xs inline-flex items-center gap-1 mt-1">
          <AlertCircle className="w-3 h-3" />
          {error}
        </span>
      )}
    </div>
  )
}
